import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle, HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import invoiceAmountCheck from "../../assets/svg/invoice-amount-check.svg";
import invoiceGenerate from "../../assets/svg/invoice-generate.svg";
import DateTimePicker from "../../components/DateTimePicker";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  EmailPreviewBlock,
  WhatsAppMessageBlock,
} from "components/InvoiceMessagePreview";
import { Checkbox } from "components/ui/checkbox";
import {
  FollowUpWorkflowStep,
  generateFollowUpInvoiceToClientPdf,
  getFollowUpDiscussionDetails,
  getFollowUpInvoicePaymentMessagePreview,
  getFollowUpInvoiceToClientPreview,
  getFollowUpProgress,
  saveFollowUpInvoiceToClient,
  sendFollowUpInvoiceToClient,
  sendFollowUpInvoicePaymentMessage,
  type FollowUpInvoiceEmailPreview,
  type FollowUpInvoiceToClientPreview,
} from "../../api/followUpDiscussion";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
const INPUT_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

function formatRupees(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "Rs. 0/-";
  return `Rs. ${Math.round(v).toLocaleString("en-IN")}/-`;
}

function formatInvoiceDateDisplay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}-${m}-${y}`;
}

function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | undefined {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function PreviewRow({
  label,
  children,
  alignTop = false,
}: {
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
}) {
  const align = alignTop ? "items-start" : "items-baseline";
  return (
    <>
      <div className={`flex ${align} font-medium text-[#222]`}>{label}</div>
      <div className={`flex ${align} text-[#222]`}>:</div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );
}

function InvoiceApplicationsTable({
  applications,
}: {
  applications: FollowUpInvoiceToClientPreview["applications"];
}) {
  return (
    <div className="overflow-x-auto rounded-[6px] border border-[#EEEEEE]">
      <table className="w-full border-collapse text-[12px] leading-5">
        <thead className="bg-[#F7F7F7]">
          <tr>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
              Application Number
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Class
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Mark
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Type of Mark
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
              Date of Filing
            </th>
            <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {applications.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-2.5 py-3 text-center text-[#6D6D6D]"
              >
                No applications.
              </td>
            </tr>
          ) : (
            applications.map((app, idx) => (
              <tr
                key={`${app.applicationNumber}-${idx}`}
                className={
                  idx < applications.length - 1
                    ? "border-b border-[#EEEEEE]"
                    : ""
                }
              >
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {app.applicationNumber?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.classNumber != null ? String(app.classNumber) : "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.mark?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.typeOfMark?.trim() || "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                  {formatInvoiceDateDisplay(app.dateOfFiling)}
                </td>
                <td className="px-2.5 py-2 text-[#222222] align-top">
                  {app.status?.trim() || "—"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceDataPreview({
  preview,
  invoiceDate,
  discountInput,
}: {
  preview: FollowUpInvoiceToClientPreview;
  invoiceDate: Date | null;
  discountInput: string;
}) {
  const addresseeLines = (preview.addressee || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const attorneyPerApp = Number(preview.attorneyChangeChargesPerApp ?? 0);
  const followUpPerApp = Number(preview.followUpChargesPerApp ?? 0);
  const chargesPerApp = attorneyPerApp + followUpPerApp;
  const liveTotalCharges = chargesPerApp * preview.noOfApplications;

  const parsedDiscount = Number(discountInput);
  const liveDiscountPerApp =
    discountInput.trim() === "" || !Number.isFinite(parsedDiscount)
      ? 0
      : Math.max(0, parsedDiscount);
  const liveTotalDiscount = liveDiscountPerApp * preview.noOfApplications;

  const liveInvoiceDateIso = toISODateString(invoiceDate) ?? null;

  return (
    <div
      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5"
      style={FORM_STYLES}
    >
      <PreviewRow label="Addressee" alignTop>
        {addresseeLines.length > 0 ? (
          <div className="flex flex-col">
            {addresseeLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </div>
        ) : (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </PreviewRow>
      <PreviewRow label="Form of Address">
        {preview.formOfAddress?.trim() || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </PreviewRow>
      <PreviewRow label="Mark">
        {preview.mark?.trim() || <span className="text-[#6D6D6D]">—</span>}
      </PreviewRow>
      <PreviewRow label="Selected Class">
        {preview.selectedClass?.trim() || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </PreviewRow>
      <PreviewRow label="Selected Type of Mark">
        {preview.selectedTypeOfMark?.trim() || (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </PreviewRow>
      <PreviewRow
        label={`Attorney Change Charges (${preview.noOfApplications} app${
          preview.noOfApplications !== 1 ? "s" : ""
        } × Rs. ${attorneyPerApp.toLocaleString("en-IN")})`}
      >
        {formatRupees(preview.totalAttorneyChangeCharges)}
      </PreviewRow>
      <PreviewRow
        label={`Follow-up Charges (${preview.noOfApplications} app${
          preview.noOfApplications !== 1 ? "s" : ""
        } × Rs. ${followUpPerApp.toLocaleString("en-IN")})`}
      >
        {formatRupees(preview.totalFollowUpCharges)}
      </PreviewRow>
      <PreviewRow label="Total Charges">
        {formatRupees(liveTotalCharges)}
      </PreviewRow>
      {liveDiscountPerApp > 0 && (
        <PreviewRow
          label={`− Discount (${preview.noOfApplications} app${
            preview.noOfApplications !== 1 ? "s" : ""
          } × Rs. ${liveDiscountPerApp.toLocaleString("en-IN")})`}
        >
          {formatRupees(liveTotalDiscount)}
        </PreviewRow>
      )}
      {preview.collectedAdvance > 0 && (
        <PreviewRow label="− Collected Advance">
          {formatRupees(preview.collectedAdvance)}
        </PreviewRow>
      )}
      <PreviewRow label="Invoice Amount">
        {formatRupees(preview.invoiceAmount)}
      </PreviewRow>
      <PreviewRow label="Invoice No. for Follow-up">
        {preview.includeInvoiceNo
          ? preview.invoiceNo?.trim() || (
              <span className="text-[#6D6D6D]">Auto-generated on save</span>
            )
          : <span className="text-[#6D6D6D]">—</span>}
      </PreviewRow>
      <PreviewRow label="Date of Invoice for Follow-up">
        {liveInvoiceDateIso ? (
          formatInvoiceDateDisplay(liveInvoiceDateIso)
        ) : (
          <span className="text-[#6D6D6D]">—</span>
        )}
      </PreviewRow>
    </div>
  );
}

/**
 * Info icon that reveals a small popover on hover/focus/tap. Tap toggles for
 * mobile (where hover doesn't fire); blur and Esc close it. Mirrors how the
 * backend computes invoice amount so the math is auditable from the UI.
 */
function InvoiceAmountTooltip({
  preview,
}: {
  preview: FollowUpInvoiceToClientPreview;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const n = preview.noOfApplications || 0;
  const attorneyPerApp = Number(preview.attorneyChangeChargesPerApp ?? 0);
  const followUpPerApp = Number(preview.followUpChargesPerApp ?? 0);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[300px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={FORM_STYLES}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            (Attorney change + Follow-up charges) × No. of applications −
            Discount − Collected Advance
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                Attorney change charge / app
              </span>
              <span className="font-medium">
                @ {formatRupees(attorneyPerApp)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Follow-up charge / app</span>
              <span className="font-medium">
                @ {formatRupees(followUpPerApp)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">No. of applications</span>
              <span className="font-medium">{n}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Total charges</span>
              <span className="font-medium">
                {formatRupees(preview.totalCharges)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">− Total discount</span>
              <span className="font-medium">
                {formatRupees(preview.totalDiscount)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">− Collected advance</span>
              <span className="font-medium">
                {formatRupees(preview.collectedAdvance)}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">Invoice amount</span>
              <span className="font-semibold text-[#222]">
                {formatRupees(preview.invoiceAmount)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type FollowUpInvoiceToClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
};

export const FOLLOW_UP_INVOICE_TO_CLIENT_FORM_ID =
  "follow-up-invoice-to-client-form";

export default function FollowUpInvoiceToClientView({
  leadId,
  onSuccess,
  onSentStatusChange,
  onBusyChange,
}: FollowUpInvoiceToClientViewProps) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const [preview, setPreview] = useState<FollowUpInvoiceToClientPreview | null>(
    null
  );
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [discountInput, setDiscountInput] = useState<string>("");
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(true);

  const [pdf, setPdf] = useState<{
    s3Key: string;
    downloadUrl: string;
    filename: string;
  } | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [paymentMessage, setPaymentMessage] = useState<string>("");
  const [emailPreview, setEmailPreview] =
    useState<FollowUpInvoiceEmailPreview | null>(null);
  const [paymentMessageLoading, setPaymentMessageLoading] = useState(false);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadRef = useRef(true);
  // Synchronous guard against double-submit: state updates are async, so two
  // fast clicks can both pass a `saving` check before React re-renders.
  const submittingRef = useRef(false);
  const lastSavedRef = useRef<{
    invoiceDate: string | undefined;
    discount: number | undefined;
    includeInvoiceNo: boolean;
  } | null>(null);

  useEffect(() => {
    onSentStatusChange?.(hasSentWhatsapp);
  }, [hasSentWhatsapp, onSentStatusChange]);

  // Lift the busy state up so the parent footer's Send & Continue / Resend
  // button can disable itself while a save/send is in flight.
  useEffect(() => {
    onBusyChange?.(saving);
  }, [saving, onBusyChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    initialLoadRef.current = true;

    Promise.all([
      getFollowUpInvoiceToClientPreview(leadId),
      getFollowUpDiscussionDetails(leadId).catch(() => null),
      getFollowUpInvoicePaymentMessagePreview(leadId).catch(() => null),
    ])
      .then(([previewData, details, paymentPreview]) => {
        if (cancelled) return;
        setPreview(previewData);
        setInvoiceDate(parseISODate(previewData.invoiceDate));
        setDiscountInput(
          previewData.discountProfFeeFollowUp != null
            ? String(previewData.discountProfFeeFollowUp)
            : ""
        );
        setIncludeInvoiceNo(Boolean(previewData.includeInvoiceNo));
        lastSavedRef.current = {
          invoiceDate: previewData.invoiceDate ?? undefined,
          discount:
            previewData.discountProfFeeFollowUp != null
              ? Math.max(0, Number(previewData.discountProfFeeFollowUp))
              : undefined,
          includeInvoiceNo: Boolean(previewData.includeInvoiceNo),
        };
        if (previewData.invoiceDocumentUrl && previewData.invoiceDocumentFilename) {
          setPdf({
            s3Key: "",
            downloadUrl: previewData.invoiceDocumentUrl,
            filename: previewData.invoiceDocumentFilename,
          });
        }
        if (details?.preliminary?.phone) {
          setPhoneNumber(details.preliminary.phone);
        }
        if (paymentPreview?.message) {
          setPaymentMessage(paymentPreview.message);
        }
        if (paymentPreview?.emailPreview) {
          setEmailPreview(paymentPreview.emailPreview);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load invoice"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          // Allow auto-save effect to fire on subsequent edits.
          setTimeout(() => {
            initialLoadRef.current = false;
          }, 0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Read step-completion to decide Send vs Save labels and Resend visibility.
  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getFollowUpProgress(leadId);
        const step = progress.steps.find(
          (s) =>
            s.slug === FollowUpWorkflowStep.FU_S1_07_InvoiceToClientForFollowUp
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
        }
      } catch {
        if (!cancelled) setHasSentWhatsapp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const refreshPreview = async () => {
    if (!leadId) return;
    try {
      const fresh = await getFollowUpInvoiceToClientPreview(leadId);
      setPreview(fresh);
      // Don't override user's in-flight invoiceDate edit; only sync on first load above.
    } catch {
      // Silently ignore — preview is non-critical and will retry on next save.
    }
  };

  // Auto-save (debounced) when preview-relevant fields change.
  useEffect(() => {
    if (!leadId) return;
    if (initialLoadRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const parsedDiscount = Number(discountInput);
    const discountForPayload =
      discountInput.trim() === "" || !Number.isFinite(parsedDiscount)
        ? undefined
        : Math.max(0, parsedDiscount);

    debounceRef.current = setTimeout(() => {
      const isoDate = toISODateString(invoiceDate);
      const saved = lastSavedRef.current;
      if (
        saved &&
        saved.invoiceDate === isoDate &&
        saved.discount === discountForPayload &&
        saved.includeInvoiceNo === includeInvoiceNo
      ) {
        return;
      }
      saveFollowUpInvoiceToClient(leadId, {
        invoiceDate: isoDate,
        discountProfFeeFollowUp: discountForPayload,
        includeInvoiceNo,
      })
        .then(() => {
          lastSavedRef.current = {
            invoiceDate: isoDate,
            discount: discountForPayload,
            includeInvoiceNo,
          };
          // Pull recomputed invoiceAmount / totalDiscount so the displayed
          // amount reflects the new discount without requiring a reload.
          void refreshPreview();
        })
        .catch((err) => {
          toast.error(
            err instanceof Error ? err.message : "Failed to auto-save invoice"
          );
        });
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDate?.getTime(), discountInput, includeInvoiceNo, leadId]);

  const validate = (): boolean => {
    if (!preview) {
      toast.error("Invoice details are still loading.");
      return false;
    }
    if (!invoiceDate) {
      toast.error("Date of Invoice is required.");
      return false;
    }
    const parsedDiscount = Number(discountInput);
    if (
      discountInput.trim() !== "" &&
      (!Number.isFinite(parsedDiscount) || parsedDiscount < 0)
    ) {
      toast.error("Discount in Prof Fee must be a number ≥ 0.");
      return false;
    }
    return true;
  };

  const persistInvoice = async (): Promise<boolean> => {
    if (!leadId) return false;
    if (!validate()) return false;
    const parsedDiscount = Number(discountInput);
    const discountForPayload =
      discountInput.trim() === "" || !Number.isFinite(parsedDiscount)
        ? undefined
        : Math.max(0, parsedDiscount);
    const currentState = {
      invoiceDate: toISODateString(invoiceDate),
      discount: discountForPayload,
      includeInvoiceNo,
    };
    const saved = lastSavedRef.current;
    if (
      saved &&
      saved.invoiceDate === currentState.invoiceDate &&
      saved.discount === currentState.discount &&
      saved.includeInvoiceNo === currentState.includeInvoiceNo
    ) {
      return true;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    try {
      await saveFollowUpInvoiceToClient(leadId, {
        invoiceDate: currentState.invoiceDate,
        discountProfFeeFollowUp: currentState.discount,
        includeInvoiceNo: currentState.includeInvoiceNo,
      });
      lastSavedRef.current = currentState;
      await refreshPreview();
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save invoice"
      );
      return false;
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    try {
      const ok = await persistInvoice();
      if (!ok) return;
      const result = await generateFollowUpInvoiceToClientPdf(leadId);
      setPdf(result);
      toast.success("Invoice PDF generated.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const ensurePdf = async (): Promise<{
    s3Key: string;
    downloadUrl: string;
    filename: string;
  } | null> => {
    if (pdf?.downloadUrl) return pdf;
    if (!leadId) return null;
    const ok = await persistInvoice();
    if (!ok) return null;
    try {
      const result = await generateFollowUpInvoiceToClientPdf(leadId);
      setPdf(result);
      return result;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
      return null;
    }
  };

  const handleDownload = async () => {
    if (!leadId || downloading) return;
    setDownloading(true);
    try {
      const generated = await ensurePdf();
      if (!generated) return;
      const result = await downloadFileFromUrlWithFallback(
        generated.downloadUrl,
        generated.filename ?? "invoice.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = () => {
    if (!leadId) return;
    setPreviewTab("details");
    setPreviewNumPages(null);
    setPreviewModalOpen(true);
    if (!pdf?.downloadUrl) {
      setPreviewPdfLoading(true);
    }
    void (async () => {
      try {
        // Must run sequentially: backend `save` wipes invoiceDocumentKey before
        // `generatePdf` re-populates it, so a parallel payment-message-preview
        // fetch can read the invoice mid-update and return null filename / URL.
        const generated = await ensurePdf();
        if (!generated) return;
        await refreshPaymentMessagePreview();
      } finally {
        setPreviewPdfLoading(false);
      }
    })();
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const refreshPaymentMessagePreview = async () => {
    if (!leadId) return;
    setPaymentMessageLoading(true);
    try {
      const res = await getFollowUpInvoicePaymentMessagePreview(leadId);
      setPaymentMessage(res.message ?? "");
      setEmailPreview(res.emailPreview ?? null);
    } catch {
      // Non-critical — keep existing.
    } finally {
      setPaymentMessageLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || submittingRef.current) return;
    if (!validate()) return;
    submittingRef.current = true;

    if (hasSentWhatsapp) {
      // Already sent: silently save without re-sending.
      setSaving(true);
      try {
        const ok = await persistInvoice();
        if (ok) {
          toast.success("Invoice saved successfully.");
          onSuccess?.(leadId);
        }
      } finally {
        setSaving(false);
        submittingRef.current = false;
      }
      return;
    }

    // First time: save then open confirm modal with payment message preview.
    setSaving(true);
    try {
      const ok = await persistInvoice();
      if (!ok) return;
      await refreshPaymentMessagePreview();
      setConfirmMode("send");
      setShowConfirmModal(true);
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  const handleConfirmProceed = async () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSaving(true);
    try {
      const [invoiceResult, paymentResult] = await Promise.allSettled([
        sendFollowUpInvoiceToClient(leadId, {
          sendWhatsApp: true,
          sendEmail: true,
        }),
        sendFollowUpInvoicePaymentMessage(leadId),
      ]);
      const invoiceOk = invoiceResult.status === "fulfilled";
      const paymentOk = paymentResult.status === "fulfilled";
      const invoiceErr =
        invoiceResult.status === "rejected"
          ? invoiceResult.reason instanceof Error
            ? invoiceResult.reason.message
            : "Failed to send invoice to client"
          : null;
      const paymentErr =
        paymentResult.status === "rejected"
          ? paymentResult.reason instanceof Error
            ? paymentResult.reason.message
            : "Failed to send payment message"
          : null;
      const isResend = confirmMode === "resend";

      if (invoiceOk && paymentOk) {
        setHasSentWhatsapp(true);
        toast.success(
          isResend
            ? "Invoice resent to client successfully."
            : "Invoice sent to client successfully."
        );
        onSuccess?.(leadId);
      } else if (invoiceOk && !paymentOk) {
        setHasSentWhatsapp(true);
        toast.warning(
          `Invoice sent to client, but payment message failed: ${paymentErr}`
        );
        onSuccess?.(leadId);
      } else if (!invoiceOk && paymentOk) {
        toast.warning(
          `Payment message sent, but sending invoice failed: ${invoiceErr}`
        );
      } else {
        toast.error(invoiceErr ?? "Failed to send invoice to client");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || submittingRef.current) return;
    if (!validate()) return;
    submittingRef.current = true;
    setSaving(true);
    try {
      const ok = await persistInvoice();
      if (!ok) return;
      await refreshPaymentMessagePreview();
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading || !preview) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">Loading invoice details...</p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_INVOICE_TO_CLIENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={FORM_STYLES}
    >
      {preview.isInvoiceStale && (
        <p className="text-[11px] text-[#D92D20]">
          Invoice is out of date because the analysis report or applications
          changed. Save again before generating the PDF.
        </p>
      )}

      {/* Row 1 (Editable + computed): Date | Invoice Amount | Discount */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Invoice for follow-up</label>
          <DateTimePicker
            value={invoiceDate}
            onChange={(d) => setInvoiceDate(d)}
            showTime={false}
            placeholder="Select Date"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <label className={LABEL_CLASS}>Invoice Amount for follow-up</label>
            <InvoiceAmountTooltip preview={preview} />
          </div>
          <input
            type="text"
            value={formatRupees(preview.invoiceAmount)}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Discount in Prof Fee for Follow up (Rs. per application)
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={discountInput}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                setDiscountInput(v);
              }
            }}
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
            placeholder="0"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      {/* Row 2 (Readonly): Invoice No. for follow-up */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Invoice No. for follow-up</label>
          <input
            type="text"
            value={preview.invoiceNo ?? ""}
            placeholder="Auto-generated on save"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Invoice Amount summary bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span
            className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate"
            style={FORM_STYLES}
          >
            INVOICE AMOUNT FOR FOLLOW-UP
          </span>
        </div>
        <span
          className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0"
          style={FORM_STYLES}
        >
          {formatRupees(preview.invoiceAmount)}
        </span>
      </div>

      {/* Include Invoice No checkbox */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="follow-up-include-invoice-no"
          checked={includeInvoiceNo}
          onCheckedChange={(checked) => setIncludeInvoiceNo(Boolean(checked))}
        />
        <label
          htmlFor="follow-up-include-invoice-no"
          className="text-xs md:text-sm text-[#222222] cursor-pointer"
          style={FORM_STYLES}
        >
          Include Invoice No. in PDF
        </label>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={FORM_STYLES}
          >
            {generating
              ? "Generating..."
              : pdf
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          Preview
        </button>
      </div>

      {/* Phone number + optional Resend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSentWhatsapp && (
          <button
            type="button"
            disabled={saving}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={
          confirmMode === "resend"
            ? "Resend invoice to client"
            : "Confirm invoice to client"
        }
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (after invoice PDF)
              </p>
              <WhatsAppMessageBlock
                message={paymentMessage}
                loading={paymentMessageLoading}
              />
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Email (with invoice PDF)
              </p>
              <EmailPreviewBlock
                preview={emailPreview}
                loading={paymentMessageLoading}
                attachmentFallback={
                  pdf ? { filename: pdf.filename, url: pdf.downloadUrl } : null
                }
              />
            </section>
          </div>
        }
        onConfirm={() => {
          void handleConfirmProceed();
        }}
        disableConfirm={saving}
      />

      {previewModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={closePreviewModal}
        >
          <div
            className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span
                className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
                style={FORM_STYLES}
              >
                {pdf?.filename ?? "Invoice preview"}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                {pdf?.downloadUrl && (
                  <a
                    href={pdf.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#2E3037] underline underline-offset-2"
                  >
                    Open in new tab
                  </a>
                )}
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                  aria-label="Close preview"
                >
                  <HiOutlineX className="size-5" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div
              className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
              role="tablist"
            >
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "details"}
                onClick={() => setPreviewTab("details")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "details"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Details
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "pdf"}
                onClick={() => setPreviewTab("pdf")}
                disabled={!pdf?.downloadUrl && !previewPdfLoading}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                  previewTab === "pdf"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                {previewPdfLoading && !pdf?.downloadUrl
                  ? "Generated PDF (loading…)"
                  : "Generated PDF"}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "message"}
                onClick={() => setPreviewTab("message")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "message"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                WhatsApp Message
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "email"}
                onClick={() => setPreviewTab("email")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "email"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Email
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
              {previewTab === "details" ? (
                <div
                  className="flex w-full max-w-3xl flex-col gap-3"
                  style={FORM_STYLES}
                >
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Invoice details
                    </p>
                    <InvoiceDataPreview
                      preview={preview}
                      invoiceDate={invoiceDate}
                      discountInput={discountInput}
                    />
                  </section>
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Applications
                    </p>
                    <InvoiceApplicationsTable
                      applications={preview.applications}
                    />
                  </section>
                </div>
              ) : previewTab === "pdf" ? (
                pdf?.downloadUrl ? (
                  <Document
                    file={pdf.downloadUrl}
                    onLoadSuccess={({ numPages: n }) => setPreviewNumPages(n)}
                    loading={
                      <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                    }
                    error={
                      <p className="text-sm text-red-600">
                        Failed to load PDF.
                      </p>
                    }
                    className="flex flex-col items-center gap-4"
                  >
                    {previewNumPages != null &&
                      Array.from({ length: previewNumPages }, (_, i) => (
                        <Page
                          key={i + 1}
                          pageNumber={i + 1}
                          width={Math.min(
                            800,
                            typeof window !== "undefined"
                              ? window.innerWidth - 48
                              : 800
                          )}
                          renderTextLayer
                          renderAnnotationLayer
                        />
                      ))}
                  </Document>
                ) : previewPdfLoading ? (
                  <p className="text-sm text-[#6d6d6d]">Generating PDF…</p>
                ) : (
                  <p className="text-sm text-[#6D6D6D]">No PDF available.</p>
                )
              ) : previewTab === "message" ? (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be sent to client WhatsApp after the invoice PDF
                  </p>
                  <WhatsAppMessageBlock
                    message={paymentMessage}
                    loading={paymentMessageLoading}
                  />
                </div>
              ) : (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be emailed to the client with the invoice PDF
                  </p>
                  <EmailPreviewBlock
                    preview={emailPreview}
                    loading={paymentMessageLoading}
                    attachmentFallback={
                      pdf ? { filename: pdf.filename, url: pdf.downloadUrl } : null
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
